export type AppShellState = "loading" | "error" | "content";

// Note: decides the top-level app shell. Only an auth error surfaces the
// blocking ErrorScreen; a live (SignalR) connection problem must NOT, so
// connection-error state is deliberately absent from the inputs here. Connection
// trouble keeps the page and is shown via a non-blocking banner instead (see
// resolveConnectionIssueKind). The initial SignalR connect still counts as
// loading, but only once the user is authenticated.
export function resolveAppShellState(opts: {
  isAuthLoading: boolean;
  isAuthenticated: boolean;
  isConnectionLoading: boolean;
  hasAuthError: boolean;
  /** No point waiting for a live connection that cannot be made. */
  isOffline?: boolean;
  /** True once the shell has rendered the app at least once this session. */
  hasShownContent?: boolean;
}): AppShellState {
  if (opts.isAuthLoading) return "loading";

  // Note: only the *first* connect may blank the shell, and never while offline. Every subsequent
  // connect attempt has to keep the page: going back to "loading" unmounts the whole app, and on
  // remount every query on the page refetches - which offline is a burst of failing requests and,
  // if the connection is flapping, an endless blank-and-reload cycle.
  const blockingConnect = opts.isAuthenticated
    && opts.isConnectionLoading
    && !opts.isOffline
    && !opts.hasShownContent;
  if (blockingConnect) return "loading";

  if (opts.hasAuthError) return "error";
  return "content";
}
